import * as THREE from 'three';

/**
 * Interface pour chaque niveau de détail.
 * Le seuil peut être défini selon un critère (ici, par exemple, le zoom de la caméra).
 */
export interface LODLevel {
    model: THREE.Object3D;
    threshold: number;
}

/**
 * LODGroup est un groupe qui contient plusieurs niveaux de détail pour un même objet.
 */
export class LODGroup extends THREE.Group {
    private lodLevels: LODLevel[] = [];

    /**
     * Ajoute un modèle associé à un seuil.
     * @param model Le modèle 3D (qui peut être lui-même un groupe de plusieurs éléments)
     * @param threshold Le seuil à partir duquel ce modèle doit être affiché.
     */
    public addLOD(model: THREE.Object3D, threshold: number): void {
        model.userData.lodThreshold = threshold;
        // Masquer le modèle par défaut
        model.visible = false;
        this.lodLevels.push({ model, threshold });
        this.add(model);
        // On trie du seuil le plus élevé au plus bas (pour privilégier le modèle le plus détaillé quand possible)
        this.lodLevels.sort((a: LODLevel, b: LODLevel): number => b.threshold - a.threshold);
    }

    /**
     * Met à jour la visibilité en fonction d’un critère (par exemple, camera.zoom).
     * @param camera La caméra orthographique utilisée dans la scène.
     */
    public updateLOD(camera: THREE.OrthographicCamera): void {
        let selectedLOD: LODLevel | null = null;
        // Parcourt les niveaux et sélectionne le premier dont le seuil est satisfait.
        for (const lod of this.lodLevels) {
            if (camera.zoom >= lod.threshold) {
                selectedLOD = lod;
                break;
            }
        }
        // Si aucun niveau n’est sélectionné, on prend le moins détaillé.
        if (!selectedLOD && this.lodLevels.length > 0) {
            selectedLOD = this.lodLevels[this.lodLevels.length - 1];
        }

        // Met à jour la visibilité : seul le niveau sélectionné est visible
        this.lodLevels.forEach(lod => {
            lod.model.visible = (lod === selectedLOD);
        });
    }
}

/**
 * (Optionnel) Un gestionnaire global pour enregistrer et mettre à jour tous les LODGroup de la scène.
 */
const registeredLODGroups: LODGroup[] = [];

/**
 * Enregistre un LODGroup dans le gestionnaire global.
 * @param lodGroup Le LODGroup à enregistrer.
 */
export function registerLODGroup(lodGroup: LODGroup): void {
    registeredLODGroups.push(lodGroup);
}

/**
 * Met à jour tous les LODGroup enregistrés.
 * À appeler dans votre boucle d’animation.
 * @param camera La caméra orthographique.
 */
export function updateAllLOD(camera: THREE.OrthographicCamera): void {
    registeredLODGroups.forEach(lodGroup => lodGroup.updateLOD(camera));
}
